import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import ContactSection from "../components/contactSection.jsx";
import ContentText from "../components/contentText.jsx";
import OrganizationJsonLd from "../components/organizationJsonLd.jsx";
import SectionContent from "../components/sectionContent.jsx";
import { OFFICES } from "../components/swedenMap.jsx";
import { SiteContentContext } from "../context/SiteContentContext";
import { UserContext } from "../context/UserContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { getTeamMembers } from "../httpService";
import { getSnapshotValue } from "../snapshot.js";
import { absoluteUrl, DEFAULT_COMPANY_EMAIL, DEFAULT_COMPANY_PHONE } from "../seoUtils";

const AVATAR_COLORS = [
  "bg-primary",
  "bg-primary700",
  "bg-primary500",
  "bg-primary300",
  "bg-gray-500",
  "bg-gray-600",
];

function getInitials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function ContactTeamCard({ member, colorIndex }) {
  const { name = "", imageUrl = "", email = "", phone = "", role = "" } = member;

  if (!name) return null;

  const colorClass = AVATAR_COLORS[colorIndex % AVATAR_COLORS.length];
  const initials = getInitials(name);

  return (
    <div className="flex flex-col">
      {/* Portrait image */}
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="w-full aspect-[4/5] object-cover object-top" />
      ) : (
        <div
          className={`w-full aspect-[4/5] flex items-center justify-center text-inverse text-4xl font-bold ${colorClass}`}
        >
          {initials}
        </div>
      )}

      {/* Info below image */}
      <div className="mt-3">
        <p className="font-bold text-sm text-text-strong uppercase tracking-wide">{name}</p>
        {role && <p className="font-bold text-sm text-primary mt-0.5">{role}</p>}
        {(phone || email) && (
          <div className="mt-2 flex flex-col gap-1 text-sm text-text-muted">
            {phone && (
              <a
                href={`tel:${phone}`}
                className="flex items-center gap-1.5 hover:text-primary transition-colors"
              >
                <span className="text-amber-500 font-bold">→</span>
                {phone}
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="flex items-center gap-1.5 hover:text-primary transition-colors"
              >
                <span className="text-amber-500 font-bold">→</span>
                {email}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const ContactPage = () => {
  const { locale } = useContext(UserContext);
  const { locale: pathLocale } = useLocalePath();
  const { getContent } = useContext(SiteContentContext);
  const [members, setMembers] = useState(() => getSnapshotValue("team", []));
  const [teamLoadFailed, setTeamLoadFailed] = useState(false);

  useEffect(() => {
    let isActive = true;

    setTeamLoadFailed(false);
    // Deliberately not clearing members first: on a server-rendered page they are already
    // seeded from the snapshot, and blanking them would flash the section away on mount.
    getTeamMembers(locale)
      .then((data) => {
        if (isActive) {
          setMembers(data);
        }
      })
      .catch(() => {
        if (isActive) {
          setMembers([]);
          setTeamLoadFailed(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [locale]);

  const phone = getContent("company.phone", DEFAULT_COMPANY_PHONE);
  const email = getContent("company.email", DEFAULT_COMPANY_EMAIL);
  const contactUrl = absoluteUrl(pathLocale, "/contact");
  const officeCities = OFFICES.map((office) =>
    getContent(`office.${office.key}.city`, office.city)
  ).join(", ");

  return (
    <>
      <Helmet>
        <title>
          AS Maskiner - Kontakt | {phone} | {email}
        </title>
        <meta
          name="description"
          content={`Kontakta AS Maskiner för frågor om nya och begagnade CNC-maskiner, kantlistmaskiner, sprutautomater, fladderputs och bredbandputsar. Vi säljer maskiner från KDT, SCM, Homag, Biesse, KA MA, AES, Viet m.fl. Ring ${phone} eller maila ${email}. Vi finns här för dig mån-fre 09-17.`}
        />
        <meta
          name="keywords"
          content="AS Maskiner kontakt, köpa nya maskiner, köpa begagnade maskiner, KDT, CNC maskiner kontakt, kantlistmaskin frågor, support, telefon, email, info@asmaskiner.se"
        />

        {/* Geo targeting for local search */}
        <meta name="geo.region" content="SE" />
        <meta name="geo.placename" content={officeCities} />

        <meta property="og:url" content={contactUrl} />
        <meta property="og:title" content="Kontakt - AS Maskiner" />
        <meta
          property="og:description"
          content={`Kontakta oss för frågor om nya och begagnade maskiner. Tel: ${phone}, Email: ${email}`}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            url: contactUrl,
            name: "Kontakt",
            description:
              "Kontakta AS Maskiner för frågor, support eller information om våra maskiner.",
          })}
        </script>
      </Helmet>

      {/* Organization + one LocalBusiness per office */}
      <OrganizationJsonLd locale={pathLocale} />

      {/* Contact form section */}
      <ContactSection />

      {!teamLoadFailed && members.length > 0 && (
        <section className="py-14 md:py-20 bg-surface-muted">
          <SectionContent>
            <h2 className="text-2xl md:text-3xl font-bold text-text-strong mb-10 text-center">
              <ContentText contentKey="contact.team_heading" fallback="Träffa teamet" />
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
              {members.map((member, i) => (
                <ContactTeamCard
                  key={member.id ?? `${member.email}-${i}`}
                  member={member}
                  colorIndex={i}
                />
              ))}
            </div>
          </SectionContent>
        </section>
      )}
    </>
  );
};

export default ContactPage;
